import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  Grid,
  TextField,
  MenuItem,
  CircularProgress,
  Backdrop,
} from '@mui/material';
import {
  Add,
  Edit,
  Cancel,
  Visibility,
  CheckCircle,
  ViewModule,
  ViewList,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import EditAppointmentDialog from '../components/EditAppointmentDialog';
import { formatTime } from '../utils/timeFormat';

const formatDuration = (minutes: number): string => {
  if (!minutes) return '0 min';
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (hours === 0) return `${remainingMinutes} min`;
  if (remainingMinutes === 0) return `${hours}h`;
  return `${hours}h ${remainingMinutes}min`;
};

const formatDate = (dateString: string): string => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

const Appointments: React.FC = () => {
  const { user } = useAuth();
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState<any>(null);
  const [viewingAppointment, setViewingAppointment] = useState<any>(null);
  const [appointments, setAppointments] = useState<any[]>([]);
  const [filteredAppointments, setFilteredAppointments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    date: new Date().toISOString().split('T')[0],
    customDate: '',
    status: '',
    client: '',
    staff: ''
  });
  const [sortBy, setSortBy] = useState<string>('appointmentDate');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  useEffect(() => {
    fetchAppointments();
  }, []);

  const fetchAppointments = async () => {
    setLoading(true);
    try {
      const response = await fetch('https://salon.rockyroadsolutions.com/api/appointments');
      if (response.ok) {
        const data = await response.json();
        
        // Debug: Log the first appointment to check if tipAmount is present
        if (data.length > 0) {
          console.log('First appointment data:', data[0]);
        }
        
        // Filter appointments based on user role
        let filteredData = data;
        if (user?.role === 'CLIENT') {
          // Show only appointments for the logged-in client
          filteredData = data.filter((apt: any) => 
            apt.client?.email === user.email
          );
        }
        
        // Ensure tipAmount is properly set for all appointments
        filteredData = filteredData.map((apt: any) => ({
          ...apt,
          tipAmount: apt.tipAmount || 0
        }));
        
        setAppointments(filteredData);
        setFilteredAppointments(filteredData);
      }
    } catch (error) {
      console.error('Error fetching appointments:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = appointments;

    if (filters.date && filters.date !== '') {
      if (filters.date === 'last7days') {
        const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        const today = new Date().toISOString().split('T')[0];
        filtered = filtered.filter(apt => {
          if (!apt.appointmentDate) return false;
          const aptDate = new Date(apt.appointmentDate).toLocaleDateString('en-CA'); // YYYY-MM-DD format
          return aptDate >= sevenDaysAgo && aptDate <= today;
        });
      } else {
        const targetDate = filters.date === 'custom' ? filters.customDate : filters.date;
        if (targetDate) {
          filtered = filtered.filter(apt => {
            if (!apt.appointmentDate) return false;
            const aptDate = new Date(apt.appointmentDate).toLocaleDateString('en-CA'); // YYYY-MM-DD format
            return aptDate === targetDate;
          });
        }
      }
    }

    if (filters.status) {
      filtered = filtered.filter(apt => apt.status === filters.status);
    }

    if (filters.client) {
      filtered = filtered.filter(apt => 
        `${apt.client?.firstName} ${apt.client?.lastName}`.toLowerCase().includes(filters.client.toLowerCase())
      );
    }

    if (filters.staff) {
      filtered = filtered.filter(apt => 
        `${apt.primaryStaff?.firstName} ${apt.primaryStaff?.lastName}`.toLowerCase().includes(filters.staff.toLowerCase())
      );
    }

    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      let result = 0;
      switch (sortBy) {
        case 'appointmentDate':
          result = new Date(a.appointmentDate).getTime() - new Date(b.appointmentDate).getTime();
          break;
        case 'createdAt':
          result = new Date(a.created_at || a.createdAt).getTime() - new Date(b.created_at || b.createdAt).getTime();
          break;
        case 'clientName':
          result = `${a.client?.firstName} ${a.client?.lastName}`.localeCompare(`${b.client?.firstName} ${b.client?.lastName}`);
          break;
        case 'staffName':
          result = `${a.primaryStaff?.firstName} ${a.primaryStaff?.lastName}`.localeCompare(`${b.primaryStaff?.firstName} ${b.primaryStaff?.lastName}`);
          break;
        case 'totalPrice':
          result = parseFloat(a.totalPrice) - parseFloat(b.totalPrice);
          break;
        case 'status':
          result = a.status.localeCompare(b.status);
          break;
        default:
          return 0;
      }
      return sortOrder === 'desc' ? -result : result;
    });
    
    setFilteredAppointments(sorted);
  };

  useEffect(() => {
    applyFilters();
  }, [filters, appointments, sortBy, sortOrder]);

  const handleUpdateAppointment = async (appointmentData: any) => {
    try {
      const response = await fetch(`https://salon.rockyroadsolutions.com/api/appointments/${appointmentData.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(appointmentData),
      });
      
      if (response.ok) {
        // Refresh appointments list
        fetchAppointments();
      }
    } catch (error) {
      console.error('Error updating appointment:', error);
    }
  };

  const handleEditAppointment = (appointment: any) => {
    setEditingAppointment(appointment);
    setIsEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setIsEditDialogOpen(false);
    setEditingAppointment(null);
  };

  const handleViewAppointment = async (appointment: any) => {
    try {
      // Fetch full appointment details including services
      const response = await fetch(`https://salon.rockyroadsolutions.com/api/appointments/${appointment.id}`);
      if (response.ok) {
        const detailedAppointment = await response.json();
        console.log('Detailed appointment data:', detailedAppointment);
        
        // Ensure tipAmount is set
        detailedAppointment.tipAmount = detailedAppointment.tipAmount || 0;
        
        setViewingAppointment(detailedAppointment);
      } else {
        // Fallback to basic appointment data if fetch fails
        appointment.tipAmount = appointment.tipAmount || 0;
        setViewingAppointment(appointment);
      }
      setIsViewDialogOpen(true);
    } catch (error) {
      console.error('Error fetching appointment details:', error);
      // Fallback to basic appointment data if fetch fails
      appointment.tipAmount = appointment.tipAmount || 0;
      setViewingAppointment(appointment);
      setIsViewDialogOpen(true);
    }
  };

  const handleStatusChange = async (appointmentId: number, newStatus: string) => {
    try {
      const response = await fetch(`https://salon.rockyroadsolutions.com/api/appointments/${appointmentId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: newStatus }),
      });
      
      if (response.ok) {
        setAppointments(prev => prev.map(apt => 
          apt.id === appointmentId ? { ...apt, status: newStatus } : apt
        ));
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'SCHEDULED': return 'default';
      case 'CONFIRMED': return 'primary';
      case 'COMPLETED': return 'success';
      case 'CANCELLED': return 'error';
      case 'NO_SHOW': return 'warning';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Backdrop open={true} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Appointments</Typography>
        <Box display="flex" gap={2}>
          <Box sx={{ border: '1px solid #E0E0E0', borderRadius: 1, p: 0.5 }}>
            <IconButton
              size="small"
              onClick={() => setViewMode('grid')}
              sx={{ color: viewMode === 'grid' ? '#E91E63' : '#666' }}
            >
              <ViewModule />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => setViewMode('list')}
              sx={{ color: viewMode === 'list' ? '#E91E63' : '#666' }}
            >
              <ViewList />
            </IconButton>
          </Box>
          {(user?.role === 'ADMIN' || user?.role === 'RECEPTIONIST') && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => window.location.href = '/book-appointment'}
            >
              New Appointment
            </Button>
          )}
        </Box>
      </Box>

      {/* Filter Section */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Filter Appointments</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              fullWidth
              label="Date Filter"
              value={filters.date}
              onChange={(e) => setFilters({...filters, date: e.target.value})}
              size="small"
            >
              <MenuItem value="">All Dates</MenuItem>
              <MenuItem value={new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().split('T')[0]}>Yesterday</MenuItem>
              <MenuItem value={new Date().toISOString().split('T')[0]}>Today</MenuItem>
              <MenuItem value={new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0]}>Tomorrow</MenuItem>
              <MenuItem value="last7days">Last 7 Days</MenuItem>
              <MenuItem value="custom">Select Date</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              fullWidth
              label="Status"
              value={filters.status}
              onChange={(e) => setFilters({...filters, status: e.target.value})}
              size="small"
            >
              <MenuItem value="">All Status</MenuItem>
              <MenuItem value="SCHEDULED">Scheduled</MenuItem>
              <MenuItem value="CONFIRMED">Confirmed</MenuItem>
              <MenuItem value="COMPLETED">Completed</MenuItem>
              <MenuItem value="CANCELLED">Cancelled</MenuItem>
              <MenuItem value="NO_SHOW">No Show</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Client Name"
              value={filters.client}
              onChange={(e) => setFilters({...filters, client: e.target.value})}
              size="small"
              placeholder="Search by client name"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Staff Name"
              value={filters.staff}
              onChange={(e) => setFilters({...filters, staff: e.target.value})}
              size="small"
              placeholder="Search by staff name"
            />
          </Grid>
          {filters.date === 'custom' && (
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Select Date"
                type="date"
                value={filters.customDate || ''}
                onChange={(e) => setFilters({...filters, customDate: e.target.value})}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </Grid>
          )}
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              select
              fullWidth
              label="Sort By"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              size="small"
            >
              <MenuItem value="appointmentDate">Appointment Date</MenuItem>
              <MenuItem value="createdAt">Creation Date</MenuItem>
              <MenuItem value="clientName">Client Name</MenuItem>
              <MenuItem value="staffName">Staff Name</MenuItem>
              <MenuItem value="totalPrice">Total Amount</MenuItem>
              <MenuItem value="status">Status</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={1}>
            <TextField
              select
              fullWidth
              label="Order"
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value as 'asc' | 'desc')}
              size="small"
            >
              <MenuItem value="desc">↓ Desc</MenuItem>
              <MenuItem value="asc">↑ Asc</MenuItem>
            </TextField>
          </Grid>
        </Grid>
        <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
          <Button 
            variant="outlined" 
            onClick={() => {
              setFilters({ date: new Date().toISOString().split('T')[0], customDate: '', status: '', client: '', staff: '' });
            }}
            size="small"
          >
            Clear Filters
          </Button>
          <Typography variant="body2" sx={{ alignSelf: 'center', ml: 2, color: 'text.secondary' }}>
            Showing {filteredAppointments.length} of {appointments.length} appointments
          </Typography>
        </Box>
      </Paper>

      {viewMode === 'grid' ? (
        <Grid container spacing={3}>
          {filteredAppointments.map((appointment) => (
            <Grid item xs={12} md={6} lg={4} key={appointment.id}>
              <Card
                sx={{
                  background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)',
                  border: '1px solid #E91E63',
                  borderRadius: 3,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 25px rgba(233, 30, 99, 0.2)',
                    borderColor: '#AD1457'
                  }
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Box>
                      <Typography variant="h6" fontWeight={600} color="#E91E63">
                        🕐 {formatTime(appointment.startTime)} - {formatTime(appointment.endTime)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        📅 {formatDate(appointment.appointmentDate)} • {formatDuration(appointment.totalDuration || 0)}
                      </Typography>
                    </Box>
                    <Chip
                      label={appointment.status}
                      color={getStatusColor(appointment.status) as any}
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                  </Box>
                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <Box sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #E91E63 0%, #AD1457 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontWeight: 'bold'
                    }}>
                      {appointment.client?.firstName?.[0]}{appointment.client?.lastName?.[0]}
                    </Box>
                    <Box>
                      <Typography variant="body1" fontWeight={600}>
                        👤 {appointment.client?.firstName} {appointment.client?.lastName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        💅 {appointment.primaryStaff?.firstName} {appointment.primaryStaff?.lastName}
                      </Typography>
                    </Box>
                  </Box>
                  <Box textAlign="center" mb={2} sx={{ p: 2, backgroundColor: '#F3E5F5', borderRadius: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Service: ${parseFloat(appointment.totalPrice).toFixed(2)}
                      {parseFloat(appointment.tipAmount || 0) > 0 && ` • Tip: $${parseFloat(appointment.tipAmount).toFixed(2)}`}
                    </Typography>
                    <Typography variant="h4" fontWeight={700} color="#9C27B0">
                      ${(parseFloat(appointment.totalPrice) + parseFloat(appointment.tipAmount || 0)).toFixed(2)}
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="center" gap={1}>
                    <Button size="small" variant="outlined" startIcon={<Visibility />} onClick={() => handleViewAppointment(appointment)} sx={{ borderColor: '#2196F3', color: '#2196F3' }}>View</Button>
                    <Button size="small" variant="outlined" startIcon={<Edit />} onClick={() => handleEditAppointment(appointment)} sx={{ borderColor: '#FF9800', color: '#FF9800' }}>Edit</Button>
                    {(user?.role === 'ADMIN' || user?.role === 'RECEPTIONIST') && appointment.status === 'SCHEDULED' && (
                      <Button size="small" variant="contained" startIcon={<CheckCircle />} onClick={() => handleStatusChange(appointment.id, 'CONFIRMED')} sx={{ backgroundColor: '#4CAF50' }}>Confirm</Button>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Box>
          {filteredAppointments.map((appointment) => (
            <Card
              key={appointment.id}
              sx={{
                mb: 2,
                background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)',
                border: '1px solid #E91E63',
                borderRadius: 2,
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 4px 15px rgba(233, 30, 99, 0.15)',
                  borderColor: '#AD1457'
                }
              }}
            >
              <CardContent sx={{ py: 2 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={2}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box sx={{
                        width: 35,
                        height: 35,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #E91E63 0%, #AD1457 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '0.9rem'
                      }}>
                        {appointment.client?.firstName?.[0]}{appointment.client?.lastName?.[0]}
                      </Box>
                      <Box>
                        <Typography variant="body2" fontWeight={600} color="#E91E63">
                          {formatTime(appointment.startTime)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(appointment.appointmentDate)}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Typography variant="body1" fontWeight={600}>
                      {appointment.client?.firstName} {appointment.client?.lastName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      with {appointment.primaryStaff?.firstName}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <Typography variant="h6" fontWeight={700} color="#9C27B0">
                      ${(parseFloat(appointment.totalPrice) + parseFloat(appointment.tipAmount || 0)).toFixed(2)}
                    </Typography>
                    {parseFloat(appointment.tipAmount || 0) > 0 && (
                      <Typography variant="caption" color="text.secondary">
                        Includes ${parseFloat(appointment.tipAmount).toFixed(2)} tip
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <Chip
                      label={appointment.status}
                      color={getStatusColor(appointment.status) as any}
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Box display="flex" gap={1}>
                      <Button size="small" variant="outlined" startIcon={<Visibility />} onClick={() => handleViewAppointment(appointment)} sx={{ borderColor: '#2196F3', color: '#2196F3', minWidth: 'auto', px: 1 }}>View</Button>
                      <Button size="small" variant="outlined" startIcon={<Edit />} onClick={() => handleEditAppointment(appointment)} sx={{ borderColor: '#FF9800', color: '#FF9800', minWidth: 'auto', px: 1 }}>Edit</Button>
                      {(user?.role === 'ADMIN' || user?.role === 'RECEPTIONIST') && appointment.status === 'SCHEDULED' && (
                        <Button size="small" variant="contained" startIcon={<CheckCircle />} onClick={() => handleStatusChange(appointment.id, 'CONFIRMED')} sx={{ backgroundColor: '#4CAF50', minWidth: 'auto', px: 1 }}>✓</Button>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}
      
      <EditAppointmentDialog
        open={isEditDialogOpen}
        onClose={handleCloseEditDialog}
        onSave={handleUpdateAppointment}
        appointment={editingAppointment}
      />
      
      {/* Enhanced View Details Dialog */}
      <Dialog 
        open={isViewDialogOpen} 
        onClose={() => setIsViewDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)'
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #E91E63 0%, #AD1457 100%)',
          color: 'white',
          textAlign: 'center',
          py: 2.5,
          boxShadow: '0 2px 8px rgba(233, 30, 99, 0.3)'
        }}>
          <Typography variant="h5" fontWeight={700} sx={{ letterSpacing: '0.5px' }}>
            ✨ Appointment Details
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ p: 2 }}>
          <Box sx={{ mt: 2 }}>
            {/* Client & Staff Section */}
            <Card sx={{ mb: 2, border: '1px solid #E91E63', borderRadius: 2 }}>
              <CardContent sx={{ py: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Box sx={{ 
                        width: 35, 
                        height: 35, 
                        borderRadius: '50%', 
                        background: 'linear-gradient(135deg, #E91E63 0%, #AD1457 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '0.9rem',
                        fontWeight: 'bold'
                      }}>
                        {viewingAppointment?.client?.firstName?.[0]}{viewingAppointment?.client?.lastName?.[0]}
                      </Box>
                      <Typography variant="subtitle1" fontWeight={600} color="#E91E63">Client</Typography>
                    </Box>
                    <Typography variant="body1" fontWeight={500}>{viewingAppointment?.client?.firstName} {viewingAppointment?.client?.lastName}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      <a href={`mailto:${viewingAppointment?.client?.email}`} style={{ color: '#1976d2', textDecoration: 'none' }}>
                        {viewingAppointment?.client?.email}
                      </a>
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      📞 +1-234-567-8900 |
                      <a href={`tel:+12345678900`} style={{ color: '#4CAF50', textDecoration: 'none', marginLeft: '5px', marginRight: '10px' }}>
                        Call
                      </a>
                      <a href={`https://wa.me/12345678900`} target="_blank" rel="noopener noreferrer" style={{ color: '#25D366', textDecoration: 'none' }}>
                        WhatsApp
                      </a>
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Box sx={{ 
                        width: 35, 
                        height: 35, 
                        borderRadius: '50%', 
                        background: 'linear-gradient(135deg, #9C27B0 0%, #6A1B9A 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '0.9rem',
                        fontWeight: 'bold'
                      }}>
                        {viewingAppointment?.primaryStaff?.firstName?.[0]}{viewingAppointment?.primaryStaff?.lastName?.[0]}
                      </Box>
                      <Typography variant="subtitle1" fontWeight={600} color="#9C27B0">Staff</Typography>
                    </Box>
                    <Typography variant="body1" fontWeight={500}>{viewingAppointment?.primaryStaff?.firstName} {viewingAppointment?.primaryStaff?.lastName}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'capitalize' }}>{viewingAppointment?.primaryStaff?.role}</Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Schedule & Payment Section */}
            <Grid container spacing={2}>
              <Grid item xs={12} md={8}>
                <Card sx={{ mb: 2, border: '1px solid #FF5722', borderRadius: 2 }}>
                  <CardContent sx={{ py: 2 }}>
                    <Typography variant="subtitle1" fontWeight={600} color="#FF5722" gutterBottom>📅 Schedule</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={4}>
                        <Typography variant="body2" color="text.secondary">Date</Typography>
                        <Typography variant="body1" fontWeight={500}>
                          {new Date(viewingAppointment?.appointmentDate).toLocaleDateString()}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="body2" color="text.secondary">Time</Typography>
                        <Typography variant="body1" fontWeight={500}>
                          {formatTime(viewingAppointment?.startTime)} - {formatTime(viewingAppointment?.endTime)}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="body2" color="text.secondary">Duration</Typography>
                        <Typography variant="body1" fontWeight={500}>
                          {formatDuration(viewingAppointment?.totalDuration || 0)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card sx={{ mb: 2, border: '1px solid #00BCD4', borderRadius: 2 }}>
                  <CardContent sx={{ py: 2, textAlign: 'center' }}>
                    <Typography variant="subtitle1" fontWeight={600} color="#00BCD4" gutterBottom>💰 Total</Typography>
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Service: ${parseFloat(viewingAppointment?.totalPrice || 0).toFixed(2)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Tip: ${parseFloat(viewingAppointment?.tipAmount || 0).toFixed(2)}
                      </Typography>
                    </Box>
                    <Typography variant="h4" fontWeight={700} color="#00BCD4">
                      ${(parseFloat(viewingAppointment?.totalPrice || 0) + parseFloat(viewingAppointment?.tipAmount || 0)).toFixed(2)}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      fontWeight={500}
                      sx={{
                        color: viewingAppointment?.status === 'COMPLETED' ? '#4CAF50' : 
                              viewingAppointment?.status === 'CANCELLED' ? '#F44336' : '#FF9800',
                        textTransform: 'capitalize'
                      }}
                    >
                      {viewingAppointment?.status?.toLowerCase()}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Services Section */}
            <Card sx={{ mb: 2, border: '1px solid #4CAF50', borderRadius: 2 }}>
              <CardContent sx={{ py: 2 }}>
                <Typography variant="subtitle1" fontWeight={600} color="#4CAF50" gutterBottom>💇‍♀️ Services</Typography>
                {viewingAppointment?.services?.length > 0 ? (
                  <Box>
                    {viewingAppointment.services.map((service: any, index: number) => (
                      <Box key={index} sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        py: 1,
                        borderBottom: index < viewingAppointment.services.length - 1 ? '1px solid #e0e0e0' : 'none'
                      }}>
                        <Box>
                          <Typography variant="body1" fontWeight={500}>{service.name}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            with {service.staff?.firstName} {service.staff?.lastName} • {service.duration} min
                          </Typography>
                        </Box>
                        <Typography variant="body1" fontWeight={600}>${service.price.toFixed(2)}</Typography>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">No services listed</Typography>
                )}
              </CardContent>
            </Card>
            
            {viewingAppointment?.notes && (
              <Card sx={{ border: '1px solid #9E9E9E', borderRadius: 2 }}>
                <CardContent sx={{ py: 2 }}>
                  <Typography variant="subtitle1" fontWeight={600} color="#666" gutterBottom>📝 Notes</Typography>
                  <Typography variant="body2">{viewingAppointment.notes}</Typography>
                </CardContent>
              </Card>
            )}
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 2, justifyContent: 'center' }}>
          <Button 
            onClick={() => setIsViewDialogOpen(false)}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #E91E63 0%, #AD1457 100%)',
              px: 3
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Appointments;
